import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Lightbulb, ArrowRight, Clock, Home, AlertCircle, ChevronRight, Activity, Play, X, BookOpen } from 'lucide-react';

// Map each pain category → full SEO guide slug + related deep-dive SEO subpages.
// "fullSlug" = main SEO landing page for that body region.
// "related"  = additional related conditions (deep-dive pages) for SEO interlinking.
const CATEGORY_LINKS = {
  hrbet:     { fullSlug: 'bolecine-v-hrbtu-fizioterapija-ljubljana',     related: ['ishias-fizioterapija-ljubljana', 'hernija-diska-fizioterapija-ljubljana', 'skolioza-fizioterapija-ljubljana'] },
  vrat:      { fullSlug: 'bolecine-v-vratu-fizioterapija-ljubljana',     related: ['glavoboli-fizioterapija-ljubljana', 'hernija-diska-fizioterapija-ljubljana', 'vrtoglavica-bppv-fizioterapija-ljubljana'] },
  rama:      { fullSlug: 'bolecine-v-rami-fizioterapija-ljubljana',      related: ['zamrznjena-rama-frozen-shoulder-ljubljana', 'teniski-komolec-fizioterapija-ljubljana', 'bolecine-v-zapestju-fizioterapija-ljubljana'] },
  koleno:    { fullSlug: 'bolecine-v-kolenu-fizioterapija-ljubljana',    related: ['bolecine-v-kolku-fizioterapija-ljubljana', 'zvin-gleznja-rehabilitacija-ljubljana', 'petni-trn-plantarni-fasciitis-ljubljana'] },
  glezenj:   { fullSlug: 'zvin-gleznja-rehabilitacija-ljubljana',        related: ['petni-trn-plantarni-fasciitis-ljubljana', 'sportne-poskodbe-prva-pomoc-ljubljana', 'bolecine-v-kolenu-fizioterapija-ljubljana'] },
  glavobol:  { fullSlug: 'glavoboli-fizioterapija-ljubljana',            related: ['vrtoglavica-bppv-fizioterapija-ljubljana', 'bolecine-v-vratu-fizioterapija-ljubljana'] },
  nosecnost: { fullSlug: 'terapija-za-nosecnice-ljubljana',              related: ['bolecine-v-hrbtu-fizioterapija-ljubljana', 'bolecine-v-kolku-fizioterapija-ljubljana'] },
  sport:     { fullSlug: 'sportne-poskodbe-prva-pomoc-ljubljana',        related: ['zvin-gleznja-rehabilitacija-ljubljana', 'teniski-komolec-fizioterapija-ljubljana', 'bolecine-v-kolenu-fizioterapija-ljubljana'] },
};

// Friendly labels for each SEO slug (shown in the related-topics list).
const SLUG_LABELS = {
  'bolecine-v-hrbtu-fizioterapija-ljubljana': 'Bolečine v hrbtu',
  'bolecine-v-vratu-fizioterapija-ljubljana': 'Bolečine v vratu',
  'bolecine-v-rami-fizioterapija-ljubljana':  'Bolečine v rami',
  'bolecine-v-kolenu-fizioterapija-ljubljana':'Bolečine v kolenu',
  'zvin-gleznja-rehabilitacija-ljubljana':    'Zvin gležnja',
  'glavoboli-fizioterapija-ljubljana':        'Glavoboli',
  'terapija-za-nosecnice-ljubljana':          'Fizioterapija za nosečnice',
  'sportne-poskodbe-prva-pomoc-ljubljana':    'Športne poškodbe — prva pomoč',
  'ishias-fizioterapija-ljubljana':           'Ishias',
  'hernija-diska-fizioterapija-ljubljana':    'Hernija diska',
  'teniski-komolec-fizioterapija-ljubljana':  'Teniški komolec',
  'karpalni-tunel-sindrom-fizioterapija-ljubljana': 'Karpalni tunel sindrom',
  'bolecine-v-kolku-fizioterapija-ljubljana': 'Bolečine v kolku',
  'petni-trn-plantarni-fasciitis-ljubljana':  'Petni trn / plantarni fasciitis',
  'bolecine-v-zapestju-fizioterapija-ljubljana':'Bolečine v zapestju',
  'zamrznjena-rama-frozen-shoulder-ljubljana':'Zamrznjena rama',
  'skolioza-fizioterapija-ljubljana':         'Skolioza',
  'vrtoglavica-bppv-fizioterapija-ljubljana': 'Vrtoglavica (BPPV)',
};

const QuickHelpSection = () => {
  const [selectedId, setSelectedId] = useState(null);

  const categories = [
    { id: 'hrbet', label: 'Bolečine v hrbtu', iconImage: '/images/icons/icon-hrbet.png' },
    { id: 'vrat', label: 'Bolečine v vratu', iconImage: '/images/icons/icon-vrat.png' },
    { id: 'rama', label: 'Bolečine v rami', iconImage: '/images/icons/icon-rama.png' },
    { id: 'koleno', label: 'Bolečine v kolenu', iconImage: '/images/icons/icon-koleno.png' },
    { id: 'glezenj', label: 'Zvin gležnja', iconImage: '/images/icons/icon-glezenj.png' },
    { id: 'glavobol', label: 'Glavoboli', iconImage: '/images/icons/icon-glava.png' },
    { id: 'nosecnost', label: 'Nosečnost', iconImage: '/images/icons/icon-nosecnice.png' },
    { id: 'sport', label: 'Športna poškodba', iconImage: '/images/icons/icon-sport.png' }
  ];

  // Individual exercise images - from uploaded ZIP
  const exerciseImages = {
    hamstring: '/exercises/vaja-razteg-zadnje-stegenske-fizioterapija.png',
    kneeToChest: '/exercises/vaja-koleno-k-prsim-fizioterapija.png',
    seatedForward: '/exercises/vaja-sed-naklon-naprej-fizioterapija.png',
    pelvicTilt: '/exercises/vaja-naklon-medenice-fizioterapija.png',
    childsPose: '/exercises/vaja-otroska-poza-fizioterapija.png',
    catCow: '/exercises/vaja-cat-cow-razteg-fizioterapija.png',
    lyingKneeTwist: '/exercises/vaja-rotacija-kolen-leze-fizioterapija.png',
    seatedSpinalTwist: '/exercises/vaja-sedeci-spinalni-zasuk-fizioterapija.png',
    kneeSways: '/exercises/vaja-zibanje-kolen-fizioterapija.png',
    childsPoseSide: '/exercises/vaja-otroska-poza-stranska-fizioterapija.png',
    sphinx: '/exercises/vaja-sfinga-razteg-fizioterapija.png',
    cobra: '/exercises/vaja-mckenzie-ekstenzija-kobra-fizioterapija.png',
    standingBackExt: '/exercises/vaja-stoje-ekstenzija-hrbta-fizioterapija.png',
    latStretch: '/exercises/vaja-stranski-razteg-fizioterapija.png',
    figureFour: '/exercises/vaja-figura-stirka-razteg-fizioterapija.png',
    piriformis: '/exercises/vaja-razteg-piriformne-misice-fizioterapija.png'
  };

  // For neck exercises, we still use the infographic crop method
  const neckExerciseImage = 'https://customer-assets.emergentagent.com/job_e19a9925-7001-4d2f-ab88-17a09e593ff5/artifacts/swkv48sy_ChatGPT%20Image%2011.%20feb.%202026%2C%2014_09_21.png';

  const getAdvice = (id) => {
    switch(id) {
      case 'hrbet':
        return {
          title: 'Nasveti za bolečine v hrbtu',
          urgent: 'Če imate mravljince v nogah ali težave z mehurjem, takoj poiščite zdravniško pomoč.',
          tips: 'Izogibajte se dolgotrajnemu sedenju. Uporabite hladen obkladek prvih 48 ur. Izvajajte nežne raztezne vaje.',
          exercisesList: [
            { name: 'Otroška poza', desc: '30 sekund', image: exerciseImages.childsPose },
            { name: 'Cat-Cow razteg', desc: '10 ponovitev', image: exerciseImages.catCow },
            { name: 'Koleno k prsim', desc: '15 sek. vsaka noga', image: exerciseImages.kneeToChest }
          ],
          waitTime: 'Do termina izvajajte vaje 2-3x dnevno po 10 minut'
        };
      case 'vrat':
        return {
          title: 'Nasveti za bolečine v vratu',
          urgent: 'Če imate vročino, glavobol ali otrplost rok, poiščite zdravniško pomoč.',
          tips: 'Prilagodite višino monitorja. Uporabite ergonomsko blazino. Naredite premore pri delu.',
          exercisesList: [
            { name: 'Nagib glave vstran', desc: '15 sek. na stran', image: neckExerciseImage, cropX: '5%', cropY: '-5%' },
            { name: 'Kroženje z vratom', desc: '5x v vsako smer', image: neckExerciseImage, cropX: '-235%', cropY: '-5%' },
            { name: 'Razteg trapezija', desc: '20 sek. vsaka stran', image: neckExerciseImage, cropX: '-115%', cropY: '-105%' }
          ],
          waitTime: 'Raztezajte vrat nežno 3x dnevno'
        };
      case 'rama':
        return {
          title: 'Nasveti za bolečine v rami',
          urgent: 'Če ne morete dvigniti roke ali imate oteklino, obiščite zdravnika.',
          tips: 'Izogibajte se gibom nad glavo. Uporabite led 15-20 minut. Spanje na zdravi strani.',
          exercisesList: [
            { name: 'Prekrižan razteg rok', desc: '20 sek. vsaka roka', image: neckExerciseImage, cropX: '-235%', cropY: '-105%' },
            { name: 'Razteg za hrbtom', desc: '20 sekund', image: neckExerciseImage, cropX: '5%', cropY: '-55%' },
            { name: 'Cat-Cow razteg', desc: '10 ponovitev', image: exerciseImages.catCow }
          ],
          waitTime: 'Led in počitek sta ključna do obiska'
        };
      case 'koleno':
        return {
          title: 'Nasveti za bolečine v kolenu',
          urgent: 'Če koleno "popusti" ali se blokira, potrebujete pregled.',
          tips: 'Počivajte in se izogibajte aktivnostim. Led 15-20 minut, 3-4x dnevno. Dvignite nogo.',
          exercisesList: [
            { name: 'Razteg zadnje stegenske', desc: '30 sek. vsaka noga', image: exerciseImages.hamstring },
            { name: 'Štirka razteg', desc: '20 sek. vsaka noga', image: exerciseImages.figureFour },
            { name: 'Razteg piriformisa', desc: '20 sek. vsaka stran', image: exerciseImages.piriformis }
          ],
          waitTime: 'Metoda RICE: Rest, Ice, Compression, Elevation'
        };
      case 'glezenj':
        return {
          title: 'Nasveti za zvin gležnja',
          urgent: 'Če ne morete stopiti na nogo ali je deformirana, pojdite na urgenco.',
          tips: 'Takoj uporabite led in kompresijo. Dvignite nogo nad nivo srca. Uporabite bergle.',
          exercisesList: [
            { name: 'Kroženje z gležnjem', desc: '10x v vsako smer', image: null },
            { name: 'Pisanje abecede s prstom', desc: '1x celotna abeceda', image: null }
          ],
          waitTime: 'Strogo mirovanje in led prvih 48 ur'
        };
      case 'glavobol':
        return {
          title: 'Nasveti za glavobole',
          urgent: 'Nenaden, hud glavobol zahteva takojšnjo zdravniško pomoč.',
          tips: 'Pijte dovolj vode. Temna, tiha soba za počitek. Hladni obkladki na čelo.',
          exercisesList: [
            { name: 'Nagib glave vstran', desc: '15 sek. na stran', image: neckExerciseImage, cropX: '5%', cropY: '-5%' },
            { name: 'Razteg trapezija', desc: '20 sek. vsaka stran', image: neckExerciseImage, cropX: '-115%', cropY: '-105%' },
            { name: 'Cat-Cow razteg', desc: '10 ponovitev', image: exerciseImages.catCow }
          ],
          waitTime: 'Vodite dnevnik glavobolov za fizioterapevta'
        };
      case 'nosecnost':
        return {
          title: 'Nasveti za nosečnice',
          urgent: 'Ob kakršnihkoli nenavadnih simptomih se posvetujte z ginekologom.',
          tips: 'Spanje na levi strani z blazino med koleni. Nosečniški pas za podporo. Redna hoja.',
          exercisesList: [
            { name: 'Cat-Cow razteg', desc: '10 ponovitev', image: exerciseImages.catCow },
            { name: 'Otroška poza', desc: '30 sekund', image: exerciseImages.childsPose },
            { name: 'Zibanje kolen', desc: '10 ponovitev', image: exerciseImages.kneeSways }
          ],
          waitTime: 'Fizioterapija je varna in priporočljiva v nosečnosti'
        };
      case 'sport':
        return {
          title: 'Nasveti za športne poškodbe',
          urgent: 'Če je prisotna deformacija ali ne morete premikati sklepa, pojdite na urgenco.',
          tips: 'Takojšnje prenehanje aktivnosti. RICE protokol. Izogibajte se toploti prvih 48 ur.',
          exercisesList: [
            { name: 'Počakajte z vajami', desc: 'Do pregleda', image: null }
          ],
          waitTime: 'Čimprejšnji pregled za pravilno diagnozo'
        };
      default:
        return null;
    }
  };

  const selectedCategory = categories.find(c => c.id === selectedId);
  const advice = selectedId ? getAdvice(selectedId) : null;

  return (
    <section 
      id="hitra-pomoc" 
      className="py-16 md:py-24 relative overflow-hidden"
      data-testid="quick-help-section"
    >
      {/* Light background with soft coral accents - same as Monthly Program */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-[#fff8f8] to-[#ffefef]" />
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#f87171]/10 rounded-full blur-[100px]" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#1a365d]/5 rounded-full blur-[80px]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-[#f87171]/10 text-[#f87171] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Lightbulb className="w-4 h-4" />
            Svetovalnica
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a365d] mb-4">
            Kaj lahko storite sami?
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Izberite vaše težave in prejmite takojšnje nasvete, kaj lahko storite doma, medtem ko čakate na termin.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            {/* Saša's image - larger */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-br from-[#f87171]/20 to-transparent rounded-full blur-xl" />
                <img loading="lazy" decoding="async"
                  src="https://customer-assets.emergentagent.com/job_sasa-physio/artifacts/vosoziye_Sasa_profil.avif"
                  alt="Saša Panič"
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 border-white shadow-xl relative z-10"
                />
              </div>
              <div>
                <p className="text-[#1a365d] font-bold text-lg">Saša Panič</p>
                <p className="text-slate-500 text-sm">Vaša fizioterapevtka</p>
              </div>
            </div>
            
            <p className="text-slate-600 mb-6 text-sm">
              Izberite področje težav za takojšnje nasvete:
            </p>

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setSelectedId('hrbet')} className={`flex items-center gap-2 px-3 py-3 rounded-xl text-left text-sm font-medium transition-all ${selectedId === 'hrbet' ? 'bg-[#f87171] text-white shadow-lg' : 'bg-white text-[#1a365d] hover:bg-[#f87171]/10 shadow-sm border border-slate-100'}`} data-testid="pain-category-hrbet">
                <img loading="lazy" decoding="async" src="/images/icons/icon-hrbet.png" alt="" width="40" height="40" className="w-9 h-9 rounded-full flex-shrink-0 object-cover" /><span className="truncate">Bolečine v hrbtu</span>
              </button>
              <button onClick={() => setSelectedId('vrat')} className={`flex items-center gap-2 px-3 py-3 rounded-xl text-left text-sm font-medium transition-all ${selectedId === 'vrat' ? 'bg-[#f87171] text-white shadow-lg' : 'bg-white text-[#1a365d] hover:bg-[#f87171]/10 shadow-sm border border-slate-100'}`} data-testid="pain-category-vrat">
                <img loading="lazy" decoding="async" src="/images/icons/icon-vrat.png" alt="" width="40" height="40" className="w-9 h-9 rounded-full flex-shrink-0 object-cover" /><span className="truncate">Bolečine v vratu</span>
              </button>
              <button onClick={() => setSelectedId('rama')} className={`flex items-center gap-2 px-3 py-3 rounded-xl text-left text-sm font-medium transition-all ${selectedId === 'rama' ? 'bg-[#f87171] text-white shadow-lg' : 'bg-white text-[#1a365d] hover:bg-[#f87171]/10 shadow-sm border border-slate-100'}`} data-testid="pain-category-rama">
                <img loading="lazy" decoding="async" src="/images/icons/icon-rama.png" alt="" width="40" height="40" className="w-9 h-9 rounded-full flex-shrink-0 object-cover" /><span className="truncate">Bolečine v rami</span>
              </button>
              <button onClick={() => setSelectedId('koleno')} className={`flex items-center gap-2 px-3 py-3 rounded-xl text-left text-sm font-medium transition-all ${selectedId === 'koleno' ? 'bg-[#f87171] text-white shadow-lg' : 'bg-white text-[#1a365d] hover:bg-[#f87171]/10 shadow-sm border border-slate-100'}`} data-testid="pain-category-koleno">
                <img loading="lazy" decoding="async" src="/images/icons/icon-koleno.png" alt="" width="40" height="40" className="w-9 h-9 rounded-full flex-shrink-0 object-cover" /><span className="truncate">Bolečine v kolenu</span>
              </button>
              <button onClick={() => setSelectedId('glezenj')} className={`flex items-center gap-2 px-3 py-3 rounded-xl text-left text-sm font-medium transition-all ${selectedId === 'glezenj' ? 'bg-[#f87171] text-white shadow-lg' : 'bg-white text-[#1a365d] hover:bg-[#f87171]/10 shadow-sm border border-slate-100'}`} data-testid="pain-category-glezenj">
                <img loading="lazy" decoding="async" src="/images/icons/icon-glezenj.png" alt="" width="40" height="40" className="w-9 h-9 rounded-full flex-shrink-0 object-cover" /><span className="truncate">Zvin gležnja</span>
              </button>
              <button onClick={() => setSelectedId('glavobol')} className={`flex items-center gap-2 px-3 py-3 rounded-xl text-left text-sm font-medium transition-all ${selectedId === 'glavobol' ? 'bg-[#f87171] text-white shadow-lg' : 'bg-white text-[#1a365d] hover:bg-[#f87171]/10 shadow-sm border border-slate-100'}`} data-testid="pain-category-glavobol">
                <img loading="lazy" decoding="async" src="/images/icons/icon-glava.png" alt="" width="40" height="40" className="w-9 h-9 rounded-full flex-shrink-0 object-cover" /><span className="truncate">Glavoboli</span>
              </button>
              <button onClick={() => setSelectedId('nosecnost')} className={`flex items-center gap-2 px-3 py-3 rounded-xl text-left text-sm font-medium transition-all ${selectedId === 'nosecnost' ? 'bg-[#f87171] text-white shadow-lg' : 'bg-white text-[#1a365d] hover:bg-[#f87171]/10 shadow-sm border border-slate-100'}`} data-testid="pain-category-nosecnost">
                <img loading="lazy" decoding="async" src="/images/icons/icon-nosecnice.png" alt="" width="40" height="40" className="w-9 h-9 rounded-full flex-shrink-0 object-cover" /><span className="truncate">Nosečnost</span>
              </button>
              <button onClick={() => setSelectedId('sport')} className={`flex items-center gap-2 px-3 py-3 rounded-xl text-left text-sm font-medium transition-all ${selectedId === 'sport' ? 'bg-[#f87171] text-white shadow-lg' : 'bg-white text-[#1a365d] hover:bg-[#f87171]/10 shadow-sm border border-slate-100'}`} data-testid="pain-category-sport">
                <img loading="lazy" decoding="async" src="/images/icons/icon-sport.png" alt="" width="40" height="40" className="w-9 h-9 rounded-full flex-shrink-0 object-cover" /><span className="truncate">Športna poškodba</span>
              </button>
            </div>

            <div className="mt-6 p-4 bg-[#1a365d]/5 rounded-xl">
              <p className="text-slate-600 text-xs flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-[#f87171]" />
                Za podrobnejša vprašanja uporabite chat z Majo v spodnjem desnem kotu.
              </p>
            </div>
          </div>

          {/* Desktop: Panel na desni strani */}
          <div className="lg:col-span-3 hidden lg:block">
            {selectedCategory && advice ? (
              <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="text-[#f87171] font-semibold text-sm uppercase tracking-wider">Nasveti</span>
                    <h3 className="text-2xl font-bold text-[#1a365d] mt-1">{advice.title}</h3>
                  </div>
                  <img loading="lazy" decoding="async" src={selectedCategory.iconImage} alt="" width="56" height="56" className="w-14 h-14 rounded-full flex-shrink-0 object-cover" />
                </div>

                <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-700 text-sm">Kdaj k zdravniku?</p>
                      <p className="text-red-600 text-sm">{advice.urgent}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#f87171]/10 rounded-lg flex items-center justify-center">
                      <Home className="w-4 h-4 text-[#f87171]" />
                    </div>
                    <h4 className="font-semibold text-[#1a365d]">Kaj lahko storite doma</h4>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed pl-10">{advice.tips}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-4 h-4 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-[#1a365d]">Priporočene vaje</h4>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pl-0 md:pl-10">
                    {advice.exercisesList?.map((exercise, idx) => (
                      <div 
                        key={idx}
                        className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-xl overflow-hidden hover:shadow-lg transition-all group"
                      >
                        {exercise.image && (
                          <div className="relative h-32 overflow-hidden bg-amber-50">
                            {exercise.cropX ? (
                              // Cropped from infographic
                              <img loading="lazy" decoding="async" 
                                src={exercise.image} 
                                alt={exercise.name}
                                className="absolute w-[350%] h-auto group-hover:scale-105 transition-transform duration-300"
                                style={{ left: exercise.cropX, top: exercise.cropY }}
                              />
                            ) : (
                              // Individual exercise image
                              <img loading="lazy" decoding="async" 
                                src={exercise.image} 
                                alt={exercise.name}
                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 p-2"
                              />
                            )}
                            <div className="absolute top-2 left-2 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md z-10">
                              {idx + 1}
                            </div>
                          </div>
                        )}
                        <div className="p-3">
                          <span className="font-medium text-[#1a365d] text-sm block">{exercise.name}</span>
                          <p className="text-emerald-600 text-xs mt-1">{exercise.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-[#1a365d] to-[#2d4a7c] rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <Clock className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-semibold text-white text-sm">Do vašega termina</span>
                  </div>
                  <p className="text-white/80 text-sm pl-10">{advice.waitTime}</p>
                </div>

                {/* "Preberi celoten vodnik" + Povezane teme — SEO interlinking */}
                {CATEGORY_LINKS[selectedId] && (
                  <div className="mb-6 p-4 bg-slate-50 rounded-2xl border border-slate-100" data-testid="related-topics-block">
                    <Link
                      to={`/${CATEGORY_LINKS[selectedId].fullSlug}`}
                      className="group flex items-center justify-between gap-3 px-4 py-3 bg-white rounded-xl border border-[#f87171]/30 hover:border-[#f87171] hover:shadow-md transition-all"
                      data-testid="full-guide-link"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 bg-[#f87171]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-4 h-4 text-[#f87171]" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-[#1a365d] text-sm">Preberi celoten vodnik</p>
                          <p className="text-slate-500 text-xs truncate">Strokovni nasveti, FAQ, vaje</p>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#f87171] flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    </Link>

                    {CATEGORY_LINKS[selectedId].related?.length > 0 && (
                      <>
                        <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mt-4 mb-2 pl-1">Povezane teme</p>
                        <div className="grid gap-1.5">
                          {CATEGORY_LINKS[selectedId].related.map((slug) => (
                            <Link
                              key={slug}
                              to={`/${slug}`}
                              className="group flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white hover:bg-slate-100 transition-colors border border-slate-100"
                              data-testid={`related-link-${slug}`}
                            >
                              <span className="text-[#1a365d] text-sm font-medium truncate group-hover:text-[#f87171] transition-colors">
                                {SLUG_LABELS[slug] || slug}
                              </span>
                              <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 group-hover:text-[#f87171] group-hover:translate-x-0.5 transition-all" />
                            </Link>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                <Button
                  onClick={() => document.querySelector('[data-testid="hero-book-btn"]')?.click()}
                  className="w-full bg-[#f87171] hover:bg-[#f87171]/90 text-white rounded-full py-4 font-semibold"
                >
                  Rezervirajte termin za strokovno obravnavo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            ) : (
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 h-full flex flex-col items-center justify-center text-center min-h-[400px] shadow-lg border border-slate-100">
                <div className="w-20 h-20 bg-[#f87171]/10 rounded-full flex items-center justify-center mb-6">
                  <Lightbulb className="w-10 h-10 text-[#f87171]" />
                </div>
                <h3 className="text-[#1a365d] text-xl font-semibold mb-3">Izberite vaše težave</h3>
                <p className="text-slate-500 max-w-sm">
                  Kliknite na eno od kategorij na levi strani in prejmite personalizirane nasvete 
                  za lajšanje težav doma.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Popup Modal */}
      {selectedCategory && advice && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 lg:hidden flex items-end"
          onClick={() => setSelectedId(null)}
        >
          <div 
            className="bg-white rounded-t-3xl w-full max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with close button */}
            <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img loading="lazy" decoding="async" src={selectedCategory.iconImage} alt="" width="44" height="44" className="w-11 h-11 rounded-full flex-shrink-0 object-cover" />
                <div>
                  <span className="text-[#f87171] font-semibold text-xs uppercase tracking-wider">Nasveti</span>
                  <h3 className="text-lg font-bold text-[#1a365d]">{advice.title}</h3>
                </div>
              </div>
              <button 
                onClick={() => setSelectedId(null)}
                className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-slate-200 transition"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <div className="p-4">
              {/* Urgent warning */}
              <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-700 text-sm">Kdaj k zdravniku?</p>
                    <p className="text-red-600 text-sm">{advice.urgent}</p>
                  </div>
                </div>
              </div>

              {/* Home tips */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-[#f87171]/10 rounded-lg flex items-center justify-center">
                    <Home className="w-4 h-4 text-[#f87171]" />
                  </div>
                  <h4 className="font-semibold text-[#1a365d]">Kaj lahko storite doma</h4>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed pl-10">{advice.tips}</p>
              </div>

              {/* Exercises with images */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Activity className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h4 className="font-semibold text-[#1a365d]">Priporočene vaje</h4>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {advice.exercisesList?.map((exercise, idx) => (
                    <div 
                      key={idx}
                      className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-xl overflow-hidden flex"
                    >
                      {exercise.image && (
                        <div className="w-28 h-28 flex-shrink-0 overflow-hidden bg-amber-50 relative">
                          {exercise.cropX ? (
                            <img loading="lazy" decoding="async" 
                              src={exercise.image} 
                              alt={exercise.name}
                              className="absolute w-[350%] h-auto"
                              style={{ left: exercise.cropX, top: exercise.cropY }}
                            />
                          ) : (
                            <img loading="lazy" decoding="async" 
                              src={exercise.image} 
                              alt={exercise.name}
                              className="w-full h-full object-contain p-2"
                            />
                          )}
                        </div>
                      )}
                      <div className="p-3 flex-1 flex flex-col justify-center">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {idx + 1}
                          </div>
                          <span className="font-medium text-[#1a365d] text-sm">{exercise.name}</span>
                        </div>
                        <p className="text-emerald-600 text-xs mt-1 pl-8">{exercise.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Wait time */}
              <div className="bg-gradient-to-r from-[#1a365d] to-[#2d4a7c] rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-semibold text-white text-sm">Do vašega termina</span>
                </div>
                <p className="text-white/80 text-sm pl-10">{advice.waitTime}</p>
              </div>

              {/* "Preberi celoten vodnik" + Povezane teme (mobile) */}
              {CATEGORY_LINKS[selectedId] && (
                <div className="mb-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <Link
                    to={`/${CATEGORY_LINKS[selectedId].fullSlug}`}
                    onClick={() => setSelectedId(null)}
                    className="flex items-center justify-between gap-3 px-4 py-3 bg-white rounded-xl border border-[#f87171]/30"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 bg-[#f87171]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-4 h-4 text-[#f87171]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1a365d] text-sm">Preberi celoten vodnik</p>
                        <p className="text-slate-500 text-xs truncate">Strokovni nasveti, FAQ, vaje</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#f87171] flex-shrink-0" />
                  </Link>

                  {CATEGORY_LINKS[selectedId].related?.length > 0 && (
                    <>
                      <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mt-4 mb-2 pl-1">Povezane teme</p>
                      <div className="grid gap-1.5">
                        {CATEGORY_LINKS[selectedId].related.map((slug) => (
                          <Link
                            key={slug}
                            to={`/${slug}`}
                            onClick={() => setSelectedId(null)}
                            className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white border border-slate-100"
                          >
                            <span className="text-[#1a365d] text-sm font-medium truncate">
                              {SLUG_LABELS[slug] || slug}
                            </span>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* CTA Button */}
              <a
                href="#narocanje"
                onClick={() => setSelectedId(null)}
                className="block w-full text-center bg-gradient-to-r from-[#f87171] to-[#f87171]/90 text-white rounded-full py-4 font-semibold shadow-lg"
              >
                Rezervirajte termin za strokovno obravnavo
                <ArrowRight className="inline-block ml-2 w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default QuickHelpSection;
